import { IconButton, Stack, Tooltip } from "@mui/material";
import { Empty, Form, Input, Select, Table, Tag } from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { AiFillEdit, AiFillSave, AiOutlineCloseCircle } from "react-icons/ai";
import "./SqlConnectorRules.css";

const DATA_TYPE = ["text", "Integer", "decimal", "date"];
const EDITABLE_TYPE = ["Y", "N"];

const handleTitle = (ele) => {
  switch (ele) {
    case "fieldname":
      return "Field Name";
    case "type":
      return "Type";
    case "sql_query":
      return "SQL QUERY";
    case "sql_name":
      return "SQL Name";
    case "field_parameter":
      return "Field Parameter";
    case "size":
      return "Size";
    default:
      return ele;
  }
};

const { Option } = Select;

const getTypeTooltip = (value) => {
  switch (value) {
    case "VARCHAR":
    case "CHAR":
      return "length";
    default:
      return value;
  }
};

const getRules = (dataIndex, title, record) => {
  if (dataIndex === "sql_name") {
    return [
      {
        required: false,
        message: `Please Input ${title}!`,
      },
    ];
  }

  if (dataIndex === "field_parameter") {
    return [
      {
        required: true,
        message: `Please Input ${title}!`,
      },
    ];
  }

  return [];
};

const filterOption = (input, option) => {
  return (option?.value ?? "").toLowerCase().includes(input.toLowerCase());
};

const onSearch = (value) => {
  console.log("search:", value);
};

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  handleChangeSql,
  handleChangeType,
  sqlQueries,
  templateData,
  fetchedHeader,
  ...restProps
}) => {
  let inputNode;
  if (inputType === "select" && dataIndex === "type") {
    inputNode = (
      <Select
        placeholder="Please select"
        onChange={(e) => handleChangeType(e, record, dataIndex)}
        getPopupContainer={(node) => node.parentNode}
      >
        {DATA_TYPE.map((option) => (
          <Option key={option} value={option} title={getTypeTooltip(option)}>
            {option}
          </Option>
        ))}
      </Select>
    );
  } else if (inputType === "select" && dataIndex === "editable") {
    inputNode = (
      <Select
        placeholder="Please select"
        onChange={(e) => handleChangeType(e, record, dataIndex)}
        getPopupContainer={(node) => node.parentNode}
      >
        {EDITABLE_TYPE.map((option) => (
          <Option key={option} value={option} title={getTypeTooltip(option)}>
            {option}
          </Option>
        ))}
      </Select>
    );
  } else if (inputType === "select" && dataIndex === "field_parameter") {
    inputNode = (
      <Select
        allowClear
        mode="multiple"
        showSearch={dataIndex === "field_parameter"}
        placeholder="Please select"
        filterOption={filterOption}
        onSearch={onSearch}
        onChange={(e) => handleChangeType(e, record, dataIndex)}
        getPopupContainer={(node) => node.parentNode}
        tagRender={(props) => {
          const { label, value, closable, onClose } = props;
          return (
            <Tag
              color="blue"
              closable={closable}
              onClose={onClose}
              style={{ marginRight: 3 }}
            >
              {label}
            </Tag>
          );
        }}
      >
        {fetchedHeader.map((option) => (
          <Option key={option.header} value={option.header}>
            {option.header}
          </Option>
        ))}
      </Select>
    );
  } else if (inputType === "select" && dataIndex === "sql_name") {
    inputNode = (
      <Select
        placeholder="Please select"
        onChange={(e) => handleChangeSql(e, record, dataIndex)}
        getPopupContainer={(node) => node.parentNode}
      >
        {sqlQueries.sql_queries.map((item) => (
          <Select.Option key={item.sql_id} value={item.sql_name}>
            {item.sql_name}
          </Select.Option>
        ))}
      </Select>
    );
  } else {
    inputNode = <Input placeholder={getTypeTooltip(record?.type)} />;
  }

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={getRules(dataIndex, title, record)}
        >
          {inputNode}
        </Form.Item>
      ) : dataIndex === "field_parameter" && record[dataIndex]?.length ? (
        <>
          {record[dataIndex].map((item) => (
            <Tag color="blue" key={item} style={{ marginRight: 3 }}>
              {item}
            </Tag>
          ))}
        </>
      ) : (
        children
      )}
    </td>
  );
};

EditableCell.propTypes = {
  children: PropTypes.any,
  dataIndex: PropTypes.string,
  editing: PropTypes.any,
  handleChangeSql: PropTypes.func,
  handleChangeType: PropTypes.func,
  index: PropTypes.any,
  inputType: PropTypes.string,
  record: PropTypes.shape({
    type: PropTypes.any,
  }),
  templateData: PropTypes.shape({
    map: PropTypes.func,
  }),
  title: PropTypes.any,
  fetchedHeader: PropTypes.any,
};

export default function PowerBiRules({
  fetchedHeader,
  sqlQueries,
  templateData,
  setTemplateData,
  loading,
  editingKey,
  setEditingKey,
  disableForm,
}) {
  const [form] = Form.useForm();
  const [, setIsEditable] = useState(true);
  const isEditing = (record) => record.key === editingKey;

  useEffect(() => {
    if (editingKey === "") {
    }
  }, [editingKey]);

  const [tableHeight, setTableHeight] = useState(null);

  function arrayToObjectWithDefaultValues(keys) {
    return Object.fromEntries(keys.map((key) => [key, ""]));
  }

  const templateColumns = templateData?.length > 0 ? templateData[0] : [];
  const resultObject = arrayToObjectWithDefaultValues(
    Object.keys(templateColumns)
  );

  const edit = (record) => {
    form.setFieldsValue({
      ...resultObject,
      ...record,
    });

    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const handleChangeType = (value, record, dataIndex) => {
    const newData = [...templateData];
    newData[record.key][dataIndex] = value;
    setTemplateData(newData);

    if (record.type === "VARCHAR" || record.type === "CHAR") {
      setIsEditable(true);
    } else {
      setIsEditable(false);
    }
  };

  const handleChangeSql = (value, record, dataIndex) => {
    const newData = [...templateData];
    if (dataIndex === "sql_name") {
      newData[record.key][dataIndex] = value;
      newData[record.key]["sql_query"] = sqlQueries.sql_queries.filter(
        (item) => item.sql_name === value
      )[0].sql_query;
    } else {
      newData[record.key][dataIndex] = value;
    }
    setTemplateData(newData);
  };

  const save = async (key) => {
    try {
      const row = await form.validateFields();
      const newData = [...templateData];
      const index = newData.findIndex((item) => key === item.key);
      if (index > -1) {
        const item = newData[index];
        const record = { ...item };
        newData.splice(index, 1, {
          ...record,
        });
        setTemplateData(newData);
        setEditingKey("");
      } else {
        newData.push(row);
        setTemplateData(newData);
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const handleEditableColumn = (item) => {
    switch (item) {
      case "type":
      case "size":
      case "editable":
        return true;
      case "sql_name":
        return true;
      case "sql_query":
        return false;
      case "field_parameter":
        return true;
      default:
        return false;
    }
  };

  let columns = [];

  if (templateData?.length > 0) {
    const keys = Object.keys(templateData[0]).filter(
      (item) => item !== "key" && item !== "size"
    );

    keys.forEach((ele) => {
      columns.push({
        title: handleTitle(ele),
        dataIndex: ele,
        editable: handleEditableColumn(ele),
      });
    });
  }

  const actionColumn = {
    title: "Action",
    dataIndex: "action",
    fixed: "right",
    render: (_, record) => {
      const editable = isEditing(record);
      return editable ? (
        <Stack direction="row" alignItems="center">
          <Tooltip title="Save">
            <IconButton
              size="medium"
              disabled={disableForm}
              color="success"
              onClick={() => save(record.key)}
            >
              <AiFillSave />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton
              size="medium"
              disabled={disableForm}
              color="error"
              onClick={cancel}
            >
              <AiOutlineCloseCircle />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : (
        <Tooltip title="Edit">
          <span>
            <IconButton
              size="medium"
              color="primary"
              disabled={editingKey !== "" || disableForm}
              onClick={() => edit(record)}
            >
              <AiFillEdit />
            </IconButton>
          </span>
        </Tooltip>
      );
    },
  };

  const updatedColumns = [...columns, { ...actionColumn }];

  const mergedColumns = updatedColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType:
          col.dataIndex === "type" ||
          col.dataIndex === "sql_name" ||
          col.dataIndex === "field_parameter"
            ? "select"
            : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        handleChangeSql,
        handleChangeType,
        sqlQueries,
        templateData,
        fetchedHeader,
      }),
    };
  });

  useEffect(() => {
    const maxHeightPercentage = 80;
    const maxHeight = (window.innerHeight * maxHeightPercentage) / 100;
    setTableHeight(maxHeight);
  }, []);

  return (
    <Form form={form} component={false}>
      <div
        className="table-container"
        style={{
          maxHeight: tableHeight,
          overflowY: tableHeight ? "auto" : "visible",
        }}
      >
        <Table
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          dataSource={templateData}
          columns={mergedColumns}
          rowClassName="hoverable-row"
          loading={
            templateData.length <= 0 && loading ? !loading && <Empty /> : false
          }
          pagination={{
            onChange: cancel,
            pageSize: 5,
          }}
          sticky
        />
      </div>
    </Form>
  );
}

PowerBiRules.propTypes = {
  fetchedHeader: PropTypes.any,
  loading: PropTypes.any,
  sqlQueries: PropTypes.any,
  setTemplateData: PropTypes.func,
  templateData: PropTypes.shape({
    length: PropTypes.number,
  }),
};
